export interface BillingPaymentLike {
  id?: string;
  status?: string;
  dueDate?: string | null;
  paymentDate?: string | null;
  value?: number;
  description?: string;
  bankSlipUrl?: string;
  invoiceUrl?: string;
  paymentLink?: string;
}

const getPaymentKey = (payment: BillingPaymentLike) => (
  String(payment.id || `${payment.status || ''}:${payment.dueDate || ''}:${payment.value || ''}:${payment.description || ''}`)
);

const PAID_STATUSES = new Set(['RECEIVED', 'CONFIRMED']);
const OPEN_STATUSES = new Set(['PENDING', 'OVERDUE']);

const normalizeStatus = (status?: string | null) => String(status || '').trim().toUpperCase();

const toDate = (value?: string | null) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const startOfToday = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

const compareAscByDueDate = (a: BillingPaymentLike, b: BillingPaymentLike) => {
  const aTime = toDate(a.dueDate)?.getTime() ?? Number.MAX_SAFE_INTEGER;
  const bTime = toDate(b.dueDate)?.getTime() ?? Number.MAX_SAFE_INTEGER;
  return aTime - bTime;
};

const compareDescByReferenceDate = (a: BillingPaymentLike, b: BillingPaymentLike) => {
  const aTime = toDate(a.paymentDate)?.getTime() ?? toDate(a.dueDate)?.getTime() ?? 0;
  const bTime = toDate(b.paymentDate)?.getTime() ?? toDate(b.dueDate)?.getTime() ?? 0;
  return bTime - aTime;
};

export const getVisibleBillingPayments = (payments: BillingPaymentLike[] = []) => {
  const today = startOfToday();
  const openPayments = payments
    .filter((payment) => OPEN_STATUSES.has(normalizeStatus(payment.status)))
    .sort(compareAscByDueDate);

  const dueOrOverduePayments = openPayments.filter((payment) => {
    const status = normalizeStatus(payment.status);
    if (status === 'OVERDUE') {
      return true;
    }

    const dueDate = toDate(payment.dueDate);
    if (!dueDate) {
      return true;
    }

    return dueDate.getTime() <= today.getTime();
  });

  const actionablePayments = dueOrOverduePayments.length > 0
    ? dueOrOverduePayments
    : openPayments.slice(0, 1);

  const actionableIds = new Set(actionablePayments.map((payment) => getPaymentKey(payment)));
  const hiddenUpcomingPayments = openPayments.filter((payment) => !actionableIds.has(getPaymentKey(payment)));

  const historyPayments = payments
    .filter((payment) => !OPEN_STATUSES.has(normalizeStatus(payment.status)))
    .sort(compareDescByReferenceDate);

  const paidHistoryPayments = historyPayments.filter((payment) => PAID_STATUSES.has(normalizeStatus(payment.status)));

  return {
    actionablePayments,
    currentPayment: actionablePayments[0] || null,
    hiddenUpcomingPayments,
    hiddenUpcomingCount: hiddenUpcomingPayments.length,
    historyPayments,
    paidHistoryPayments
  };
};
